import React, { useCallback, useContext } from "react";
import {
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Text,
  ChoiceList,
  Banner,
  Box,
} from "@shopify/polaris";
import { ResourcePicker } from "@shopify/app-bridge/actions";
import { AppBridgeContext } from "../AppBridgeProvider";
import {
  DISPLAY_CRITERIA_MODE_CHOICES,
  TRIGGER_CONDITION_CHOICES,
} from "../../settings/displayCriteria";
import TagInputField from "./TagInputField";

function handlesFromSelection(items) {
  return items
    .map((x) => x.handle)
    .filter(Boolean)
    .join(", ");
}

export default function DisplayConditionSection({ displayCriteria, onPatch }) {
  const app = useContext(AppBridgeContext);
  const dc = displayCriteria || {};

  const mode = dc.mode || "all_pages";
  const triggerCondition = dc.triggerCondition === "logged_customer" ? "logged_customer" : "always";
  const urls = Array.isArray(dc.urls) ? dc.urls : [];
  const selectedProducts = Array.isArray(dc.selectedProducts) ? dc.selectedProducts : [];
  const selectedCollections = Array.isArray(dc.selectedCollections) ? dc.selectedCollections : [];

  const runCollectionPicker = useCallback(() => {
    if (!app) return;
    try {
      const initialSelectionIds = selectedCollections
        .filter((c) => c.id)
        .map((c) => ({ id: c.id }));
      const picker = ResourcePicker.create(app, {
        resourceType: ResourcePicker.ResourceType.Collection,
        options: {
          selectMultiple: true,
          initialSelectionIds,
        },
      });
      picker.subscribe(ResourcePicker.Action.SELECT, (payload) => {
        const selection = payload.selection || [];
        const next = selection.map((s) => ({
          id: s.id,
          handle: s.handle,
          title: s.title,
        }));
        onPatch({
          selectedCollections: next,
          collectionHandles: handlesFromSelection(next),
        });
        picker.unsubscribe();
      });
      picker.subscribe(ResourcePicker.Action.CANCEL, () => picker.unsubscribe());
      picker.dispatch(ResourcePicker.Action.OPEN);
    } catch (e) {
      console.error("[DisplayCondition] collection picker", e);
    }
  }, [app, onPatch, selectedCollections]);

  const runProductPicker = useCallback(() => {
    if (!app) return;
    try {
      const initialSelectionIds = selectedProducts
        .filter((p) => p.id)
        .map((p) => ({ id: p.id }));
      const picker = ResourcePicker.create(app, {
        resourceType: ResourcePicker.ResourceType.Product,
        options: {
          selectMultiple: true,
          showVariants: false,
          initialSelectionIds,
        },
      });
      picker.subscribe(ResourcePicker.Action.SELECT, (payload) => {
        const selection = payload.selection || [];
        const next = selection.map((s) => ({
          id: s.id,
          handle: s.handle,
          title: s.title,
        }));
        onPatch({
          selectedProducts: next,
          productHandles: handlesFromSelection(next),
        });
        picker.unsubscribe();
      });
      picker.subscribe(ResourcePicker.Action.CANCEL, () => picker.unsubscribe());
      picker.dispatch(ResourcePicker.Action.OPEN);
    } catch (e) {
      console.error("[DisplayCondition] product picker", e);
    }
  }, [app, onPatch, selectedProducts]);

  const removeCollection = (index) => {
    const next = selectedCollections.filter((_, i) => i !== index);
    onPatch({
      selectedCollections: next,
      collectionHandles: handlesFromSelection(next),
    });
  };

  const removeProduct = (index) => {
    const next = selectedProducts.filter((_, i) => i !== index);
    onPatch({
      selectedProducts: next,
      productHandles: handlesFromSelection(next),
    });
  };

  return (
    <BlockStack gap="400">
      <Text as="h3" variant="headingSm">
        Condition
      </Text>
      {!app ? (
        <Banner tone="warning" title="Resource picker needs Shopify Admin">
          Open this app embedded in Shopify Admin (with <code>host</code> in the URL) to pick products and
          collections. Custom URLs and tags still work here.
        </Banner>
      ) : null}

      <ChoiceList
        title="Trigger condition"
        choices={TRIGGER_CONDITION_CHOICES.map((c) => ({
          label: c.label,
          value: c.value,
        }))}
        selected={[triggerCondition]}
        onChange={(next) =>
          onPatch({ triggerCondition: next[0] === "logged_customer" ? "logged_customer" : "always" })
        }
      />
      {triggerCondition === "logged_customer" ? (
        <Text as="p" variant="bodySm" tone="subdued">
          Show the age gate only when the shopper is logged in (storefront must detect customer session).
        </Text>
      ) : (
        <Text as="p" variant="bodySm" tone="subdued">
          Show the age gate whenever page rules match, regardless of login state.
        </Text>
      )}

      <ChoiceList
        title="Display page(s)"
        choices={DISPLAY_CRITERIA_MODE_CHOICES.map((c) => ({
          label: c.label,
          value: c.value,
        }))}
        selected={[mode]}
        onChange={(next) => onPatch({ mode: next[0] || "all_pages" })}
      />

      {mode === "home" ? (
        <Text as="p" variant="bodySm" tone="subdued">
          The age gate will show only on the storefront home page (index).
        </Text>
      ) : null}

      {mode === "collections" ? (
        <BlockStack gap="300">
          <Text as="p" variant="bodySm" tone="subdued">
            Only on the matching collection page URL (<code>/collections/…</code>), not on individual product
            pages.
          </Text>
          <Button onClick={runCollectionPicker} disabled={!app}>
            Browse collections
          </Button>
          {selectedCollections.length === 0 ? (
            <Text as="p" variant="bodySm" tone="subdued">
              No collections selected. Choose one or more from your store.
            </Text>
          ) : (
            <BlockStack gap="200">
              {selectedCollections.map((c, i) => (
                <Box key={c.id || `${c.handle}-${i}`} padding="200" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                    <BlockStack gap="050">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {c.title || c.handle || "Collection"}
                      </Text>
                      {c.handle ? (
                        <Text as="span" variant="bodySm" tone="subdued">
                          /{c.handle}
                        </Text>
                      ) : null}
                    </BlockStack>
                    <Button tone="critical" variant="plain" onClick={() => removeCollection(i)}>
                      Remove
                    </Button>
                  </InlineStack>
                </Box>
              ))}
            </BlockStack>
          )}
        </BlockStack>
      ) : null}

      {mode === "products" ? (
        <BlockStack gap="300">
          <Button onClick={runProductPicker} disabled={!app}>
            Browse products
          </Button>
          {selectedProducts.length === 0 ? (
            <Text as="p" variant="bodySm" tone="subdued">
              No products selected. Choose one or more from your store.
            </Text>
          ) : (
            <BlockStack gap="200">
              {selectedProducts.map((p, i) => (
                <Box key={p.id || `${p.handle}-${i}`} padding="200" background="bg-surface-secondary" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                    <BlockStack gap="050">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {p.title || p.handle || "Product"}
                      </Text>
                      {p.handle ? (
                        <Text as="span" variant="bodySm" tone="subdued">
                          /products/{p.handle}
                        </Text>
                      ) : null}
                    </BlockStack>
                    <Button tone="critical" variant="plain" onClick={() => removeProduct(i)}>
                      Remove
                    </Button>
                  </InlineStack>
                </Box>
              ))}
            </BlockStack>
          )}
        </BlockStack>
      ) : null}

      {mode === "product_tags" ? (
        <TagInputField
          label="Tags"
          tooltip="Type a tag and press Enter. The age gate shows on product pages where the product has any of these tags."
          value={dc.productTags || ""}
          onChange={(v) => onPatch({ productTags: v })}
        />
      ) : null}

      {mode === "custom" ? (
        <BlockStack gap="200">
          <Text as="p" variant="bodySm" tone="subdued">
            Add paths or full URLs (one per line is fine in a single field, or use multiple rows). Matching is
            applied in the storefront script.
          </Text>
          {urls.map((u, i) => (
            <InlineStack key={i} gap="200" wrap={false} blockAlign="end">
              <div style={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label={`URL ${i + 1}`}
                  value={u}
                  onChange={(v) => {
                    const next = [...urls];
                    next[i] = v;
                    onPatch({ urls: next });
                  }}
                  autoComplete="off"
                />
              </div>
              <Button
                tone="critical"
                onClick={() => onPatch({ urls: urls.filter((_, j) => j !== i) })}
              >
                Remove
              </Button>
            </InlineStack>
          ))}
          <Button onClick={() => onPatch({ urls: [...urls, ""] })}>Add URL</Button>
        </BlockStack>
      ) : null}

      {mode === "products" && (dc.collectionHandles?.trim() || dc.productTags?.trim()) ? (
        <Text as="p" variant="bodySm" tone="subdued">
          Note: This template still has collection handles and/or product tags in saved JSON from the previous
          editor. The storefront script may use them until you save again with only the options you need.
        </Text>
      ) : null}
    </BlockStack>
  );
}
